/**
 * React hooks for Basecamp data fetching
 * Uses simple useState/useEffect pattern following existing codebase style
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchWithAuth } from '@/lib/api';
import { basecampApi, fetchBasecampApi, withOrganizationId } from '@/lib/basecamp-api';
import { showSyncProgress, showResumePrompt, checkPausedSync, clearPausedSync } from '@/components/integrations/basecamp/sync-progress-toast';
import type {
  BasecampProject,
  BasecampTodo,
  BasecampTodoList,
  BasecampComment,
  BasecampActivity,
  BasecampAssignment,
  BasecampAuthStatus
} from '@/types/basecamp';

/** Latest auth-status per workspace, shared by every useBasecampAuth instance. */
const basecampAuthByOrg = new Map<string, BasecampAuthStatus>();
const basecampAuthInflight = new Map<string, Promise<BasecampAuthStatus>>();
const basecampAuthListeners = new Set<() => void>();

function basecampAuthKey(organizationId?: string) {
  return organizationId || '__default__';
}

function publishBasecampAuth(organizationId: string | undefined, status: BasecampAuthStatus) {
  basecampAuthByOrg.set(basecampAuthKey(organizationId), status);
  basecampAuthListeners.forEach((fn) => fn());
}

function loadBasecampAuth(organizationId?: string): Promise<BasecampAuthStatus> {
  const key = basecampAuthKey(organizationId);
  const inflight = basecampAuthInflight.get(key);
  if (inflight) return inflight;
  const promise = basecampApi
    .getAuthStatus(organizationId)
    .then((status) => {
      publishBasecampAuth(organizationId, status);
      return status;
    })
    .catch(() => {
      const status: BasecampAuthStatus = { authenticated: false, apiUnavailable: true };
      publishBasecampAuth(organizationId, status);
      return status;
    })
    .finally(() => {
      basecampAuthInflight.delete(key);
    });
  basecampAuthInflight.set(key, promise);
  return promise;
}

export function useBasecampAuth(organizationId?: string) {
  const [authStatus, setAuthStatus] = useState<BasecampAuthStatus | null>(
    () => basecampAuthByOrg.get(basecampAuthKey(organizationId)) ?? null,
  );
  const [isLoading, setIsLoading] = useState(
    () => !basecampAuthByOrg.has(basecampAuthKey(organizationId)),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const sync = () => {
      const next = basecampAuthByOrg.get(basecampAuthKey(organizationId)) ?? null;
      setAuthStatus(next);
      if (next) setIsLoading(false);
    };
    basecampAuthListeners.add(sync);
    sync();
    return () => {
      basecampAuthListeners.delete(sync);
    };
  }, [organizationId]);

  const refetch = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      await loadBasecampAuth(organizationId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check auth status');
    } finally {
      setIsLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    void refetch();

    const onAuthChange = () => {
      void refetch();
    };
    window.addEventListener('basecamp-auth-success', onAuthChange);
    window.addEventListener('basecamp-auth-invalidated', onAuthChange);

    // Keep polling after a successful check. A mounted list used to stop
    // polling once authenticated, so an expired token still painted cache
    // while a freshly mounted sidebar already showed Connect.
    const pollMs = authStatus?.apiUnavailable ? 60_000 : authStatus?.authenticated ? 30_000 : 10_000;
    const pollInterval = setInterval(() => {
      void refetch();
    }, pollMs);

    return () => {
      clearInterval(pollInterval);
      window.removeEventListener('basecamp-auth-invalidated', onAuthChange);
      window.removeEventListener('basecamp-auth-success', onAuthChange);
    };
  }, [authStatus?.authenticated, authStatus?.apiUnavailable, refetch, organizationId]);

  return { authStatus, isLoading, error, refetch };
}

export function isBasecampDisconnected(status: BasecampAuthStatus | null | undefined): boolean {
  return Boolean(status && !status.authenticated && !status.apiUnavailable);
}

/** Re-render when any watched workspace publishes a new auth status. */
export function useBasecampAuthWatch(organizationIds: string[]) {
  const idsKey = organizationIds.join('\n');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const sync = () => setTick((n) => n + 1);
    basecampAuthListeners.add(sync);
    return () => {
      basecampAuthListeners.delete(sync);
    };
  }, []);

  void tick;

  const rows = organizationIds.map((id) => ({
    id,
    status: basecampAuthByOrg.get(basecampAuthKey(id)) ?? null,
  }));
  const disconnected = rows.filter((row) => isBasecampDisconnected(row.status));
  return { rows, disconnected, idsKey };
}

export function useBasecampProjects(organizationId?: string) {
  const [projects, setProjects] = useState<BasecampProject[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const { authStatus } = useBasecampAuth(organizationId);

  const fetchProjects = async (forceRefresh = false) => {
    // Don't make API calls if not authenticated
    if (authStatus && !authStatus.authenticated) {
      setError({
        status: 401,
        auth_url: authStatus.auth_url,
        message: 'Not authenticated'
      });
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await basecampApi.getProjects(organizationId, forceRefresh);
      setProjects(data);
    } catch (err) {
      // Preserve the full error object to access status, auth_url, etc.
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch if authenticated (or auth status is still loading)
    if (authStatus === null) {
      // Still loading auth status, wait
      return;
    }

    if (authStatus.authenticated) {
      fetchProjects();
    } else {
      // Not authenticated, set error immediately without making API call
      setError({
        status: 401,
        auth_url: authStatus.auth_url,
        message: 'Not authenticated'
      });
      setIsLoading(false);
    }
  }, [authStatus?.authenticated, organizationId]);

  // Listen for auth success events (when user completes OAuth in new tab)
  useEffect(() => {
    const handleAuthSuccess = () => {
      fetchProjects();
    };

    // Listen for custom auth success event
    window.addEventListener('basecamp-auth-success', handleAuthSuccess);

    // Also listen for focus events (user might have completed auth in another tab)
    const handleFocus = () => {
      if (error?.status === 401) {
        fetchProjects();
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('basecamp-auth-success', handleAuthSuccess);
      window.removeEventListener('focus', handleFocus);
    };
  }, [error?.status]);

  return { projects, isLoading, error, refetch: fetchProjects };
}

export function useBasecampTodos(projectId: string | null) {
  const [todos, setTodos] = useState<BasecampTodo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTodos = async (forceRefresh = false) => {
    if (!projectId) {
      setTodos([]);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await basecampApi.getTodos(projectId, forceRefresh);
      setTodos(data);
    } catch (err) {
      console.error(`[useBasecampTodos] Error fetching todos for project ${projectId}:`, err);
      setError(err instanceof Error ? err.message : 'Failed to fetch todos');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTodos();
  }, [projectId]);

  return { todos, isLoading, error, refetch: fetchTodos };
}

// Session-only todo-lists fetch state (Postgres is SoT - no localStorage payload cache)
interface CachedTodoListsData {
  todoLists: BasecampTodoList[];
  lastFetchTimestamp: string;
  projectId: string;
  cachedAt: string;
  fromServerCache?: boolean;
}

/** Survives preview-pane unmount so switching projects keeps "still fetching" UI accurate. */
const todoListsInflight = new Map<string, Promise<CachedTodoListsData>>()
const todoListsInflightListeners = new Set<() => void>()

function notifyTodoListsInflight() {
  todoListsInflightListeners.forEach((listener) => listener())
}

export function isTodoListsFetchInflight(
  projectId: string | null | undefined,
): boolean {
  return Boolean(projectId && todoListsInflight.has(projectId))
}

export function subscribeTodoListsInflight(listener: () => void): () => void {
  todoListsInflightListeners.add(listener)
  return () => {
    todoListsInflightListeners.delete(listener)
  }
}

/**
 * Network-fetch todo lists (Postgres cache-first on the API).
 * Dedupes concurrent callers (preview pane + parent Load) by projectId.
 * Does not write localStorage - org SoT is basecamp_cache_metadata.
 */
export function ensureTodoListsCached(
  projectId: string,
  forceRefresh = false,
): Promise<CachedTodoListsData> {
  const existing = !forceRefresh ? todoListsInflight.get(projectId) : undefined
  if (existing) return existing

  const pending = (async () => {
    const data = await basecampApi.getTodoLists(projectId, forceRefresh)
    const cachedAt =
      data._cached_at ||
      data._fetched_at ||
      data.timestamp ||
      new Date().toISOString()
    return {
      todoLists: data.todoLists || [],
      lastFetchTimestamp: data.timestamp || cachedAt,
      projectId,
      cachedAt,
      fromServerCache: Boolean(data._cached),
    } satisfies CachedTodoListsData
  })().finally(() => {
    if (todoListsInflight.get(projectId) === pending) {
      todoListsInflight.delete(projectId)
      notifyTodoListsInflight()
    }
  })

  todoListsInflight.set(projectId, pending)
  notifyTodoListsInflight()
  return pending
}

export type BasecampFetchOptions = {
  /** When false, do not network-fetch. Default true. */
  enabled?: boolean
  /**
   * When false, never background-refresh over the network after the first fetch.
   * Cache miss still fetches when enabled. Default true.
   */
  autoRefresh?: boolean
}

/**
 * @deprecated Prefer project.cacheDepth / listsFresh from GET /api/basecamp/projects.
 * Always false - list payload SoT is Postgres, not localStorage.
 */
export function hasCachedTodoLists(_projectId: string | null | undefined): boolean {
  return false
}

export function useBasecampTodoLists(
  projectId: string | null,
  options?: BasecampFetchOptions,
) {
  const enabled = options?.enabled !== false;
  // Kept for effect deps / call-site compatibility (preview uses autoRefresh: false).
  const autoRefresh = options?.autoRefresh !== false;
  void autoRefresh;
  const [todoLists, setTodoLists] = useState<BasecampTodoList[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const [cached, setCached] = useState(false);
  const [cachedAt, setCachedAt] = useState<string | null>(null);

  const fetchTodoLists = async (forceRefresh = false) => {
    if (!projectId) {
      setTodoLists([]);
      setCached(false);
      setCachedAt(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setApiMessage(null);

      console.log(`[useBasecampTodoLists] Fetching todo lists for project ${projectId}`, { forceRefresh });
      const cacheData = await ensureTodoListsCached(projectId, forceRefresh);

      console.log(`[useBasecampTodoLists] Received data:`, {
        todoListsCount: cacheData.todoLists?.length || 0,
        timestamp: cacheData.lastFetchTimestamp,
        project_id: cacheData.projectId,
        fromServerCache: cacheData.fromServerCache,
      });

      setTodoLists(cacheData.todoLists || []);
      setApiMessage(null);
      setCached(Boolean(cacheData.fromServerCache));
      setCachedAt(cacheData.cachedAt);
    } catch (err) {
      console.error(`[useBasecampTodoLists] Error fetching todo lists for project ${projectId}:`, err);
      setError(err instanceof Error ? err.message : 'Failed to fetch todo lists');
      setTodoLists([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) {
      setTodoLists([]);
      setCached(false);
      setCachedAt(null);
      setError(null);
      setApiMessage(null);
      setIsLoading(false);
      return;
    }

    setTodoLists([]);
    setCached(false);
    setCachedAt(null);
    setIsLoading(Boolean(enabled));

    if (!enabled) {
      return;
    }

    fetchTodoLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: refetch on project/enabled/autoRefresh only
  }, [projectId, enabled, autoRefresh]);

  return {
    todoLists,
    isLoading,
    error,
    apiMessage,
    cached,
    cachedAt,
    refetch: fetchTodoLists
  };
}

export function useBasecampComments(todoUrl: string | null) {
  const [comments, setComments] = useState<BasecampComment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!todoUrl) {
      setComments([]);
      return;
    }

    const fetchComments = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await basecampApi.getComments(todoUrl);
        setComments(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch comments');
      } finally {
        setIsLoading(false);
      }
    };

    fetchComments();
  }, [todoUrl]);

  return { comments, isLoading, error };
}

/**
 * @deprecated Activity payload SoT is Postgres. Always false.
 */
export function hasCachedProjectActivity(
  _projectId: string | null | undefined,
  _days: number = 7,
): boolean {
  return false
}

export function useBasecampProjectActivity(
  projectId: string | null,
  days: number = 7,
  options?: BasecampFetchOptions,
) {
  const enabled = options?.enabled !== false;
  const autoRefresh = options?.autoRefresh !== false;
  void autoRefresh;
  const [activities, setActivities] = useState<BasecampActivity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cached, setCached] = useState(false);
  const [cachedAt, setCachedAt] = useState<string | null>(null);

  const fetchActivities = async (forceRefresh = false) => {
    if (!projectId) {
      setActivities([]);
      setCached(false);
      setCachedAt(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const data = await basecampApi.getProjectActivity(
        projectId,
        days,
        forceRefresh,
      );

      const finalActivities = data.activities || [];
      const finalTimestamp =
        data._cached_at || new Date().toISOString();

      console.log('[useBasecampProjectActivity] Fetch', {
        count: finalActivities.length,
        cached: data._cached,
      });

      setActivities(finalActivities);
      setCached(Boolean(data._cached));
      setCachedAt(finalTimestamp);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch project activity');
      setActivities([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) {
      setActivities([]);
      setCached(false);
      setCachedAt(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    setActivities([]);
    setCached(false);
    setCachedAt(null);
    setIsLoading(Boolean(enabled));

    if (!enabled) {
      return;
    }

    fetchActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: refetch on project/days/enabled/autoRefresh only
  }, [projectId, days, enabled, autoRefresh]);

  return {
    activities,
    isLoading,
    error,
    cached,
    cachedAt,
    refetch: fetchActivities
  };
}

// Request deduplication: prevent multiple simultaneous calls to the same endpoint
let assignmentsRequestPromise: Promise<any> | null = null;
let lastAssignmentsRequestKey: string | null = null;

export function useBasecampAssignments(bookmarkedOnly = false, organizationId?: string) {
  const { authStatus } = useBasecampAuth(organizationId);
  const [assignments, setAssignments] = useState<BasecampAssignment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cached, setCached] = useState(false);
  const [cachedAt, setCachedAt] = useState<string | null>(null);
  const [stale, setStale] = useState(false);

  const fetchAssignments = async (forceRefresh = false, projectLimit?: number, projectId?: string | null) => {
    if (authStatus !== null && !authStatus.authenticated) {
      assignmentsRequestPromise = null;
      lastAssignmentsRequestKey = null;
      setAssignments([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    // Check if sync was paused
    if (checkPausedSync()) {
      console.log('[useBasecampAssignments] Sync was paused, skipping auto-fetch');
      setIsLoading(false);
      return;
    }

    let progress: ReturnType<typeof showSyncProgress> | null = null;
    const abortController = new AbortController();

    try {
      setIsLoading(true);
      setError(null);

      // Create a unique key for this request
      const requestKey = `${forceRefresh}-${projectLimit}-${bookmarkedOnly}-${projectId ?? 'all'}-${organizationId ?? ''}`;

      // If there's already a pending request with the same params, wait for it
      if (assignmentsRequestPromise && lastAssignmentsRequestKey === requestKey) {
        const data = await assignmentsRequestPromise;
        setAssignments(data.assignments || []);
        setCached(data._cached || false);
        setCachedAt(data._cached_at || null);
        setStale(data._stale || false);
        setIsLoading(false);
        return;
      }

      // Show progress toast for force refresh (initial sync or manual refresh)
      // Cancel stops in-flight request; Restart sync starts a new fetch without reload
      if (forceRefresh) {
        progress = showSyncProgress({
          onPause: () => abortController.abort(),
          onRestart: () => fetchAssignments(true),
        });
      }

      // Use Reports API (single fast call) for normal load.
      // For project-scoped sync or monitored-only, fall back to slow detailed endpoint.
      const useReportsApi = !projectId && !bookmarkedOnly;

      if (useReportsApi) {
        lastAssignmentsRequestKey = requestKey;
        assignmentsRequestPromise = basecampApi.getAssignments(forceRefresh, organizationId);
        const data = await assignmentsRequestPromise;
        assignmentsRequestPromise = null;
        lastAssignmentsRequestKey = null;
        setAssignments(data.assignments || []);
        setCached(data._cached || false);
        setCachedAt(data._cached_at || null);
        setStale(data._stale || false);
      } else {
        // projectId / bookmarkedOnly: use slow detailed endpoint (e.g. Sync All on project page)
        lastAssignmentsRequestKey = requestKey;
        assignmentsRequestPromise = basecampApi.getMyAssignmentsDetailed(
          forceRefresh,
          projectLimit,
          bookmarkedOnly,
          projectId,
          abortController.signal,
          organizationId
        );
        const data = await assignmentsRequestPromise;
        assignmentsRequestPromise = null;
        lastAssignmentsRequestKey = null;
        if (projectId && data.assignments?.length > 0) {
          setAssignments(prev => {
            const others = prev.filter(a => a?.project?.id?.toString() !== projectId.toString());
            return [...others, ...data.assignments];
          });
        } else {
          setAssignments(data.assignments || []);
        }
        setCached(data._cached || false);
        setCachedAt(data._cached_at || null);
        setStale(data._stale || false);
      }

      // Mark sync as complete
      if (progress) {
        progress.complete();
      }
    } catch (err) {
      // Clear the promise on error/abort
      assignmentsRequestPromise = null;
      lastAssignmentsRequestKey = null;

      // AbortError = user clicked Cancel sync - not a real error
      if (err instanceof Error && err.name === 'AbortError') {
        console.log('[useBasecampAssignments] Sync cancelled by user');
        if (progress) {
          progress.cancel();
        }
        setError(null);
        return;
      }

      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch assignments';
      setError(errorMessage);

      if (progress) {
        progress.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Check for paused sync on mount; skip Reports API until Basecamp is connected.
  useEffect(() => {
    if (authStatus === null) {
      return;
    }
    if (!authStatus.authenticated) {
      assignmentsRequestPromise = null;
      lastAssignmentsRequestKey = null;
      setAssignments([]);
      setError(null);
      setIsLoading(false);
      return;
    }
    if (checkPausedSync()) {
      showResumePrompt(() => {
        clearPausedSync();
        fetchAssignments(true);
      });
    } else {
      fetchAssignments();
    }
  }, [bookmarkedOnly, organizationId, authStatus?.authenticated]); // Re-fetch when filter, workspace, or Basecamp auth changes

  return {
    assignments,
    isLoading,
    error,
    cached,
    cachedAt,
    stale,
    refetch: (forceRefresh = false, projectLimit?: number, projectId?: string | null) =>
      fetchAssignments(forceRefresh, projectLimit, projectId)
  };
}

interface ProjectStats {
  active: number;
  overdue: number;
  completed: number;
  workSpecs?: number;
  cached_at: string | null;
  _cached: boolean;
}

export function useBasecampProjectStats(projectId: string | null, organizationId?: string) {
  const [stats, setStats] = useState<ProjectStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) {
      setStats(null);
      return;
    }

    const fetchStats = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetchBasecampApi(
          withOrganizationId(`/api/basecamp/projects/${projectId}/stats`, organizationId)
        );
        if (!response.ok) {
          throw new Error('Failed to fetch project stats');
        }

        const data = await response.json();
        setStats(data);
      } catch (err) {
        console.error('[useBasecampProjectStats] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch stats');
        // Set default values on error
        setStats({
          active: 0,
          overdue: 0,
          completed: 0,
          workSpecs: 0,
          cached_at: null,
          _cached: false
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [projectId, organizationId]);

  return { stats, isLoading, error };
}

interface TodoUpdateCheck {
  needsUpdate: boolean;
  changes: {
    status?: { old: string; new: string };
    due_date?: { old: string | null; new: string | null };
    assignee?: { old: string | null; new: string | null };
    updated_at?: { old: string; new: string };
  };
  todo?: {
    id: number;
    title: string;
    status: string;
    due_date: string | null;
    assignee: string | null;
    updated_at: string;
  };
}

export function useBasecampTodoUpdateCheck(
  todoUrl: string | null,
  localFrontmatter: {
    status?: string;
    due_date?: string;
    assignee?: string;
    modification_date?: string;
  } | null
) {
  const [updateCheck, setUpdateCheck] = useState<TodoUpdateCheck | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!todoUrl || !localFrontmatter) {
      setUpdateCheck(null);
      return;
    }

    const checkForUpdates = async () => {
      try {
        setIsChecking(true);
        setError(null);

        const response = await fetchWithAuth('/api/basecamp/check-todo-updates', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            todoUrl,
            localFrontmatter,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to check for updates');
        }

        const data = await response.json();

        if (data.success) {
          setUpdateCheck({
            needsUpdate: data.needsUpdate,
            changes: data.changes || {},
            todo: data.todo,
          });
        } else {
          throw new Error(data.error || 'Update check failed');
        }
      } catch (err) {
        console.error('[useBasecampTodoUpdateCheck] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to check updates');
        setUpdateCheck(null);
      } finally {
        setIsChecking(false);
      }
    };

    checkForUpdates();
  }, [todoUrl, localFrontmatter]);

  return { updateCheck, isChecking, error };
}

interface TodoWithWorkSpec {
  todoId: string;
  title: string;
  completed: boolean;
  dueOn: string | null;
  hasWorkSpec: boolean;
  workSpecId: string | null;
  workSpecTitle: string | null;
  workSpecPath: string | null;
}

export function useBasecampTodosWithWorkSpecs(projectId: string | null, organizationId?: string) {
  const [todos, setTodos] = useState<TodoWithWorkSpec[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) {
      setTodos([]);
      return;
    }

    const fetchTodos = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await basecampApi.getTodosWithWorkSpecs(projectId, 100, organizationId);
        setTodos(data.todos || []);
        // Clear error if we got a successful (even if empty) response
        setError(null);
      } catch (err) {
        // Only log error, don't set it (graceful degradation)
        console.warn('[useBasecampTodosWithWorkSpecs] Error:', err);
        setTodos([]);
        setError(null); // Don't show error to user - feature degrades gracefully
      } finally {
        setIsLoading(false);
      }
    };

    fetchTodos();
  }, [projectId, organizationId]);

  return { todos, isLoading, error };
}
